import React, { Component, createRef } from 'react';
import { Text } from 'react-konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { TextElement as TextElementData } from '../../../views/LabelBuilder/LabelBuilder.slice';
import Konva from 'konva';

interface Props {
  element: TextElementData;
  onClick: (event: KonvaEventObject<MouseEvent>) => void;
  onTransform: (event: KonvaEventObject<MouseEvent>) => void;
  onDrag: (event: KonvaEventObject<DragEvent>) => void;
  onRightClick: (event: KonvaEventObject<PointerEvent>) => void;
  onTextUpdate: (id: string, textNode: Konva.Text, newText: string) => void; // Add id to the callback
}

export default class TextElement extends Component<Props> {
  textRef = createRef<any>();

  handleDblClick = () => {
    const textNode = this.textRef.current;
    if (!textNode) return;

    const stage = textNode.getStage();
    if (!stage) return;

    const { x, y } = textNode.getAbsolutePosition();
    const container = stage.container();

    // Create and style a textarea element
    const textarea = this.createTextarea(textNode, {
      x: container.offsetLeft + x,
      y: container.offsetTop + y,
    });

    // Handle user input in textarea
    textarea.addEventListener('input', (e) => {
      textNode.text((e.target as HTMLTextAreaElement).value);
    });

    // Add event listeners to clean up and update the text node
    textarea.addEventListener('blur', () => this.removeTextarea(textarea, textNode));
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        this.removeTextarea(textarea, textNode);
      }
    });

    // Hide the text node and append textarea to the DOM
    textNode.hide();
    document.body.appendChild(textarea);
    textarea.focus();
  };

  createTextarea(textNode: any, position: { x: number; y: number }): HTMLTextAreaElement {
    const textarea = document.createElement('textarea');
    textarea.value = textNode.text();
    Object.assign(textarea.style, this.getTextareaStyle(textNode, position));
    return textarea;
  }

  getTextareaStyle(textNode: any, position: { x: number; y: number }): React.CSSProperties {
    const rotation = textNode.rotation();
    const isFirefox = /firefox/i.test(navigator.userAgent);
    const offset = isFirefox ? 2 + Math.round(textNode.fontSize() / 20) : 0;

    return {
      position: 'absolute',
      top: `${position.y}px`,
      left: `${position.x}px`,
      width: `${textNode.width() - textNode.padding() * 2}px`,
      height: `${textNode.height() - textNode.padding() * 2 + 5}px`,
      fontSize: `${textNode.fontSize()}px`,
      fontFamily: textNode.fontFamily(),
      lineHeight: `${textNode.lineHeight()}`,
      textAlign: textNode.align(),
      color: textNode.fill(),
      background: 'none',
      border: 'none',
      padding: '0',
      margin: '0',
      overflow: 'hidden',
      outline: 'none',
      resize: 'none',
      transform: `rotateZ(${rotation}deg) translateY(-${offset}px)`,
      zIndex: 1000,
    };
  }

  removeTextarea(textarea: HTMLTextAreaElement, textNode: any) {
    const newText = textarea.value;

    textNode.text(newText);
    textNode.show();
    textarea.remove();

    if (textNode.getStage()) {
      textNode.getStage().container().focus();
    }

    this.props.onTextUpdate(this.props.element.id, textNode, newText);
  }

  render() {
    const { element } = this.props;

    return (
      <Text
        ref={this.textRef}
        opacity={element.opacity}
        id={element.id}
        key={element.id}
        draggable={true}
        text={element.text}
        x={element.x}
        y={element.y}
        scaleX={element.scaleX}
        scaleY={element.scaleY}
        rotation={element.rotation}
        fontFamily={element.font.family}
        fontSize={element.font.size}
        fontStyle={element.font.style}
        fill={element.font.color}
        align={element.align}
        onClick={this.props.onClick}
        onTransform={this.props.onTransform}
        onDragEnd={this.props.onDrag}
        onContextMenu={this.props.onRightClick} 
        onDblClick={this.handleDblClick}
      />
    );
  }
}
